import { Button } from "@/components";
import { useTranslation } from "@/contexts/LanguageContext";
import { MessageSquareText, Sparkles, X } from "lucide-react";
import React from "react";
import { useAds } from "../../hooks/AdsContext";

const ContextHintsInput: React.FC = () => {
  const {
    contextHints,
    addContextHint,
    removeContextHint,
    suggesting,
    suggestedTargeting,
    handleSuggestTargeting,
    applySuggestedTargeting,
  } = useAds();
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState("");

  const commit = () => {
    addContextHint(draft);
    setDraft("");
  };

  return (
    <div className="flex-1 flex flex-col bg-white rounded-2xl p-4 md:p-5 border border-emerald-200/70 shadow-xs space-y-4">
      <div className="flex items-start gap-2 border-b pb-3 border-emerald-100">
        <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-emerald-600 rounded-lg text-white shadow-xs">
          <MessageSquareText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 h-8 flex flex-col justify-between">
          <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
            {t("Contexto de Conversa")}
          </h2>
          <span className="text-[10px] text-gray-400 font-medium leading-none">
            {t("Palavras-chave de tópico — não é segmentação demográfica")}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={handleSuggestTargeting}
          disabled={suggesting}
          className="min-h-0! h-8 px-2.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-lg font-bold text-[10px] uppercase tracking-wider whitespace-nowrap shrink-0"
          icon={
            <Sparkles
              className={`w-3 h-3 ${suggesting ? "animate-pulse" : ""}`}
            />
          }
        >
          {suggesting ? t("Analisando...") : t("Sugerir com IA")}
        </Button>
      </div>

      <p className="text-[11px] text-gray-500 leading-relaxed">
        {t(
          "O ChatGPT Ads segmenta pelo CONTEXTO da conversa do usuário, não por idade, gênero ou interesses. Informe palavras-chave de tópicos relacionados ao seu negócio (ex: 'gestão de redes sociais', 'automação de marketing') para que seu anúncio apareça em conversas relevantes — ou use o botão acima para a IA sugerir com base no perfil da empresa (ICP).",
        )}
      </p>

      {suggestedTargeting?.context_hints?.length > 0 && (
        <div className="p-3 bg-indigo-50/40 rounded-xl border border-indigo-100 space-y-2 animate-in fade-in duration-300">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">
              {t("Sugestão da IA")}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={applySuggestedTargeting}
              className="min-h-0! py-1! px-2.5! bg-indigo-600 hover:bg-indigo-500 text-white border-none font-bold text-[10px] uppercase tracking-wider rounded-lg shadow-xs whitespace-nowrap"
            >
              {t("Aplicar Tudo")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestedTargeting.context_hints.map(
              (hint: string, idx: number) => (
                <button
                  key={`${hint}-${idx}`}
                  type="button"
                  onClick={() => addContextHint(hint)}
                  disabled={contextHints.includes(hint)}
                  className="px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700 text-[11px] font-bold hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-default transition-colors"
                >
                  {hint}
                </button>
              ),
            )}
          </div>
          {suggestedTargeting.rationale && (
            <p className="text-[10px] text-indigo-400 leading-relaxed pt-1 border-t border-indigo-100/70">
              {suggestedTargeting.rationale}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <label className="text-[10px] font-bold text-gray-700 uppercase tracking-tight">
          {t("Palavras-chave de Contexto")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            placeholder={t("Ex: automação de marketing")}
            className="flex-1 h-10 px-3.5 text-sm rounded-xl border border-emerald-200 bg-white focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 outline-hidden transition-colors"
          />
          <button
            type="button"
            onClick={commit}
            disabled={!draft.trim()}
            className="px-4 h-10 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {t("Adicionar")}
          </button>
        </div>

        {contextHints.length === 0 ? (
          <p className="text-[10px] text-amber-600 font-medium pt-1">
            {t("Adicione ao menos uma palavra-chave de contexto.")}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {contextHints.map((hint, idx) => (
              <span
                key={`${hint}-${idx}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold"
              >
                {hint}
                <button
                  type="button"
                  onClick={() => removeContextHint(idx)}
                  className="hover:text-emerald-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextHintsInput;
